<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import ReportsAPI from 'dashboard/api/reports';
import ContactsAPI from 'dashboard/api/contacts';
import {
  downloadCsvFile,
  generateFileName,
} from 'dashboard/helper/downloadHelper';
import CompactDateInboxFilters from './components/CompactDateInboxFilters.vue';

const { t } = useI18n();
const store = useStore();

const EXPORT_MODE = {
  OVERVIEW: 'overview',
  AGENT: 'agent',
  CUSTOMER: 'customer',
  CONVERSATION: 'conversation',
};

const MAX_EXPORT_ROWS = 25000;

const from = ref(0);
const to = ref(0);
const selectedInboxId = ref(null);
const selectedStatus = ref('all');
const selectedMode = ref(EXPORT_MODE.OVERVIEW);

const selectedAgentId = ref('');
const includeAllAgents = ref(false);

const customerQuery = ref('');
const selectedContactId = ref(null);
const selectedContactLabel = ref('');
const contactSearchResults = ref([]);
const isContactSearching = ref(false);

const conversationIdInput = ref('');
const isDownloading = ref(false);
let searchTimer = null;
let latestSearchToken = 0;

const agents = useMapGetter('agents/getAgents');

const isAgentMode = computed(() => selectedMode.value === EXPORT_MODE.AGENT);
const isCustomerMode = computed(
  () => selectedMode.value === EXPORT_MODE.CUSTOMER
);
const isConversationMode = computed(
  () => selectedMode.value === EXPORT_MODE.CONVERSATION
);

const selectedAgent = computed(
  () => agents.value?.find(a => a.id === Number(selectedAgentId.value)) || null
);

const normalizedConversationId = computed(() => {
  const value = String(conversationIdInput.value || '')
    .trim()
    .replace(/^#/, '');
  return value;
});

const resolvedContactId = computed(() => {
  if (Number(selectedContactId.value) > 0) {
    return Number(selectedContactId.value);
  }

  const query = String(customerQuery.value || '').trim();
  if (/^\d+$/.test(query)) {
    const parsed = Number(query);
    return parsed > 0 ? parsed : null;
  }

  return null;
});

const canDownload = computed(() => {
  if (!from.value || !to.value) return false;
  if (isAgentMode.value) return Boolean(selectedAgent.value?.id);
  if (isCustomerMode.value) return Boolean(resolvedContactId.value);
  if (isConversationMode.value) return Boolean(normalizedConversationId.value);
  return true;
});

const downloadHint = computed(() => {
  if (isAgentMode.value) {
    return 'Select a date range and one agent to enable export.';
  }
  if (isCustomerMode.value) {
    return 'Select a date range and customer (name or ID) to enable export.';
  }
  if (isConversationMode.value) {
    return 'Select a date range and conversation ID to enable export.';
  }
  return 'Select a date range to enable export.';
});

const onFilterChange = ({ from: f, to: tt, selectedInbox }) => {
  from.value = f;
  to.value = tt;
  selectedInboxId.value = selectedInbox?.id || null;
};

const clearCustomerSelection = () => {
  selectedContactId.value = null;
  selectedContactLabel.value = '';
  contactSearchResults.value = [];
  isContactSearching.value = false;
};

const onSelectMode = mode => {
  selectedMode.value = mode;

  if (mode !== EXPORT_MODE.AGENT) {
    selectedAgentId.value = '';
    includeAllAgents.value = false;
  }
  if (mode !== EXPORT_MODE.CUSTOMER) {
    customerQuery.value = '';
    clearCustomerSelection();
  }
  if (mode !== EXPORT_MODE.CONVERSATION) {
    conversationIdInput.value = '';
  }

  if (searchTimer) {
    clearTimeout(searchTimer);
    searchTimer = null;
  }
};

const runContactSearch = async query => {
  latestSearchToken += 1;
  const token = latestSearchToken;
  isContactSearching.value = true;
  try {
    const response = await ContactsAPI.search(query, 1, 'name');
    if (token !== latestSearchToken) return;
    const rows = Array.isArray(response?.data?.payload)
      ? response.data.payload
      : [];
    contactSearchResults.value = rows.map(contact => ({
      id: contact.id,
      name: contact.name || `Customer #${contact.id}`,
      identifier: contact.identifier || '',
    }));
  } catch {
    if (token !== latestSearchToken) return;
    contactSearchResults.value = [];
  } finally {
    if (token === latestSearchToken) {
      isContactSearching.value = false;
    }
  }
};

const scheduleContactSearch = query => {
  if (searchTimer) {
    clearTimeout(searchTimer);
  }
  searchTimer = setTimeout(() => {
    runContactSearch(query);
  }, 300);
};

const selectContact = contact => {
  selectedContactId.value = contact.id;
  selectedContactLabel.value = contact.name;
  customerQuery.value = contact.name;
  contactSearchResults.value = [];
};

watch(customerQuery, value => {
  if (!isCustomerMode.value) return;

  const query = String(value || '').trim();
  if (query !== selectedContactLabel.value) {
    selectedContactId.value = null;
  }

  if (!query) {
    if (searchTimer) {
      clearTimeout(searchTimer);
      searchTimer = null;
    }
    contactSearchResults.value = [];
    isContactSearching.value = false;
    return;
  }

  // Allow direct numeric input for customer ID without remote search.
  if (/^\d+$/.test(query)) {
    if (searchTimer) {
      clearTimeout(searchTimer);
      searchTimer = null;
    }
    contactSearchResults.value = [];
    isContactSearching.value = false;
    return;
  }

  scheduleContactSearch(query);
});

const download = async () => {
  if (!canDownload.value) {
    useAlert(downloadHint.value);
    return;
  }

  const payload = {
    since: from.value,
    until: to.value,
    inboxId: selectedInboxId.value,
    status: selectedStatus.value,
    limit: MAX_EXPORT_ROWS,
  };

  if (isAgentMode.value) {
    payload.agentId = selectedAgent.value?.id;
    payload.includeAllAgents = includeAllAgents.value;
  } else if (isCustomerMode.value) {
    payload.contactId = resolvedContactId.value;
  } else if (isConversationMode.value) {
    payload.conversationId = normalizedConversationId.value;
  }

  isDownloading.value = true;
  try {
    const response = await ReportsAPI.getChatHistoryExport(payload);
    const fileName = generateFileName({ type: 'chat-history', to: to.value });
    downloadCsvFile(fileName, response.data);
    useAlert('Export ready - your download has started.');
  } catch (error) {
    let message = t('REPORT.DATA_FETCHING_FAILED');
    try {
      const raw = error?.response?.data;
      const text = raw instanceof Blob ? await raw.text() : JSON.stringify(raw);
      const json = JSON.parse(text);
      if (json?.error) message = json.error;
    } catch {
      // ignore parse errors, use default message
    }
    useAlert(message);
  } finally {
    isDownloading.value = false;
  }
};

onMounted(() => {
  store.dispatch('agents/get');
  store.dispatch('inboxes/get');
});

onBeforeUnmount(() => {
  if (searchTimer) {
    clearTimeout(searchTimer);
  }
});
</script>

<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<template>
  <div class="che-page">
    <div class="che-shell">
      <div class="che-header">
        <div class="che-header-left">
          <h1 class="che-title">Chat History Export</h1>
        </div>
      </div>

      <div class="che-toolbar">
        <CompactDateInboxFilters
          :show-add-filter="false"
          @filter-change="onFilterChange"
        />
      </div>

      <div class="che-content">
        <div class="che-grid">
          <div class="che-col-main">
            <section class="che-card">
              <div class="che-card-header">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  width="20"
                  height="20"
                >
                  <path
                    stroke="currentColor"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M4 6h16M4 12h16M4 18h16"
                  />
                </svg>
                <span>Configure export filters</span>
              </div>
              <div class="che-card-body">
                <div class="che-field">
                  <label class="che-label" for="che-status">Status</label>
                  <select
                    id="che-status"
                    v-model="selectedStatus"
                    class="che-select"
                  >
                    <option value="all">All</option>
                    <option value="open">Open</option>
                    <option value="resolved">Resolved</option>
                  </select>
                  <p class="che-hint">
                    Open includes active, pending, and snoozed conversations.
                  </p>
                </div>

                <div class="che-field">
                  <label class="che-label">Filter by</label>
                  <div class="che-mode-group">
                    <label class="che-mode-option">
                      <input
                        type="radio"
                        name="che-export-mode"
                        :checked="selectedMode === 'overview'"
                        @change="onSelectMode('overview')"
                      />
                      <span>All conversations (Overview)</span>
                    </label>
                    <label class="che-mode-option">
                      <input
                        type="radio"
                        name="che-export-mode"
                        :checked="selectedMode === 'agent'"
                        @change="onSelectMode('agent')"
                      />
                      <span>By Agent</span>
                    </label>
                    <label class="che-mode-option">
                      <input
                        type="radio"
                        name="che-export-mode"
                        :checked="selectedMode === 'customer'"
                        @change="onSelectMode('customer')"
                      />
                      <span>By Customer</span>
                    </label>
                    <label class="che-mode-option">
                      <input
                        type="radio"
                        name="che-export-mode"
                        :checked="selectedMode === 'conversation'"
                        @change="onSelectMode('conversation')"
                      />
                      <span>By Conversation ID</span>
                    </label>
                  </div>
                </div>

                <div v-if="isAgentMode" class="che-field">
                  <label class="che-label" for="che-agent">
                    Agent <span class="che-req">*</span>
                  </label>
                  <select
                    id="che-agent"
                    v-model="selectedAgentId"
                    class="che-select"
                  >
                    <option value="">— Select one agent —</option>
                    <option
                      v-for="agent in agents || []"
                      :key="agent.id"
                      :value="agent.id"
                    >
                      {{ agent.name }}
                    </option>
                  </select>
                  <label class="che-check-wrap">
                    <input
                      v-model="includeAllAgents"
                      type="checkbox"
                      class="che-check"
                    />
                    <span>Include other agents' messages</span>
                  </label>
                  <p class="che-hint">
                    Includes all customer messages. By default, only selected
                    agent replies are exported.
                  </p>
                </div>

                <div v-if="isCustomerMode" class="che-field">
                  <label class="che-label" for="che-customer">
                    Customer <span class="che-req">*</span>
                  </label>
                  <div class="che-customer-wrap">
                    <input
                      id="che-customer"
                      v-model="customerQuery"
                      type="text"
                      class="che-input"
                      placeholder="Search customer by name or ID"
                      autocomplete="off"
                    />
                    <div
                      v-if="
                        customerQuery &&
                        !/^[0-9]+$/.test(customerQuery) &&
                        (isContactSearching || contactSearchResults.length)
                      "
                      class="che-customer-results"
                    >
                      <div
                        v-if="isContactSearching"
                        class="che-customer-result che-customer-result--muted"
                      >
                        Searching customers...
                      </div>
                      <button
                        v-for="contact in contactSearchResults"
                        :key="contact.id"
                        class="che-customer-result"
                        type="button"
                        @click="selectContact(contact)"
                      >
                        <span class="che-customer-name">{{
                          contact.name
                        }}</span>
                        <span class="che-customer-meta">
                          ID: {{ contact.id }}
                        </span>
                      </button>
                      <div
                        v-if="
                          !isContactSearching && !contactSearchResults.length
                        "
                        class="che-customer-result che-customer-result--muted"
                      >
                        No customer found
                      </div>
                    </div>
                  </div>
                  <div v-if="resolvedContactId" class="che-selected-hint">
                    Using customer ID: {{ resolvedContactId }}
                  </div>
                  <p class="che-hint">
                    Type a customer ID directly, or search by customer name.
                  </p>
                </div>

                <div v-if="isConversationMode" class="che-field">
                  <label class="che-label" for="che-conversation-id">
                    Conversation ID <span class="che-req">*</span>
                  </label>
                  <input
                    id="che-conversation-id"
                    v-model="conversationIdInput"
                    type="text"
                    class="che-input"
                    placeholder="Enter conversation ID"
                  />
                  <p class="che-hint">
                    You can paste an ID with or without # prefix.
                  </p>
                </div>

                <button
                  class="che-dl-primary"
                  type="button"
                  :disabled="!canDownload || isDownloading"
                  @click="download"
                >
                  <svg
                    v-if="!isDownloading"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    width="18"
                    height="18"
                  >
                    <path
                      stroke="currentcolor"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2m-8-1V4m0 12 5-5m-5 5-5-5"
                    />
                  </svg>
                  <span>{{
                    isDownloading ? 'Preparing export…' : 'Download CSV'
                  }}</span>
                </button>
                <p v-if="!canDownload" class="che-dl-hint">
                  {{ downloadHint }}
                </p>
              </div>
            </section>
          </div>

          <div class="che-col-side">
            <section class="che-info-card">
              <div class="che-info-icon">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  width="18"
                  height="18"
                >
                  <path
                    stroke="#0076f5"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M12 8h.01M11 12h1v4h1M3 12a9 9 0 1 0 18.001 0A9 9 0 0 0 3 12"
                  />
                </svg>
                <span class="che-info-title">Export notes</span>
              </div>
              <ul class="che-info-list">
                <li>
                  Overview mode exports all customer and agent messages in the
                  selected date range.
                </li>
                <li>
                  Agent mode keeps old behavior by default, with an option to
                  include other agents in matched conversations.
                </li>
                <li>
                  By Customer and By Conversation ID export complete message
                  timelines across involved agents.
                </li>
                <li>
                  Maximum 25,000 message rows per export. If your file contains
                  exactly 25,000 rows, data may be incomplete — narrow your
                  date range to export all records.
                </li>
              </ul>
            </section>

            <section class="che-fmt-card">
              <div class="che-fmt-title">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  width="18"
                  height="18"
                >
                  <path
                    stroke="currentColor"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5.586a1 1 0 0 1 .707.293l5.414 5.414a1 1 0 0 1 .293.707V19a2 2 0 0 1-2 2z"
                  />
                </svg>
                CSV format
              </div>
              <ul class="che-fmt-list">
                <li><span class="che-fmt-col">Date</span> Message date</li>
                <li><span class="che-fmt-col">Time</span> Local export time</li>
                <li>
                  <span class="che-fmt-col">Conversation Status</span>
                  Open or resolved at export time
                </li>
                <li>
                  <span class="che-fmt-col">Customer Name</span>
                  Display name of the customer
                </li>
                <li>
                  <span class="che-fmt-col">Customer Search ID</span>
                  Channel identifier e.g. LINE UID
                </li>
                <li>
                  <span class="che-fmt-col">Agent Name</span>
                  Includes all involved agents when applicable
                </li>
                <li>
                  <span class="che-fmt-col">Message Type</span>
                  Text, image, audio, sticker, and more
                </li>
                <li>
                  <span class="che-fmt-col">Message Text</span>
                  Friendly text or placeholders like [Image]
                </li>
                <li>
                  <span class="che-fmt-col">Attachment URLs</span>
                  File links when present. URLs may expire after some time.
                </li>
              </ul>
            </section>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<!-- eslint-enable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->

<style scoped>
.che-page {
  min-height: 100vh;
  min-height: 100dvh;
  width: 100%;
  box-sizing: border-box;
  background: #ffffff;
  color: #111827;
}

.che-shell {
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #ffffff;
  overflow: hidden;
}

/* ── Header ── */
.che-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 58px;
  padding: 0 24px;
  border-bottom: 1px solid #eceff3;
}

.che-header-left {
  display: flex;
  align-items: center;
  gap: 0;
}

.che-title {
  margin: 0;
  font-size: 18px !important;
  line-height: 24px;
  font-weight: 600;
  letter-spacing: -0.02em;
  color: #111827;
}

.che-dl-btn {
  height: 36px;
  border-radius: 8px;
  border: 1px solid #d1d5db;
  background: #ffffff;
  color: #111827;
  padding: 0 14px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
}

.che-dl-btn:hover:not(:disabled) {
  background: #f9fafb;
}
.che-dl-btn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

/* ── Toolbar ── */
.che-toolbar {
  padding: 12px 24px 8px;
  background: #ffffff;
  box-shadow: rgba(0, 0, 0, 0.3) 0 3px 10px -6px;
}

/* ── Content ── */
.che-content {
  padding: 20px 32px 40px;
}

.che-grid {
  display: grid;
  grid-template-columns: 1fr 380px;
  gap: 20px;
  align-items: start;
}

/* ── Main column: agent card ── */
.che-card {
  border-radius: 8px;
  border: 1px solid #d8dee5;
  background: #ffffff;
  overflow: hidden;
}

.che-card-header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 14px 20px;
  border-bottom: 1px solid #dfe3e8;
  font-size: 16px;
  font-weight: 600;
  color: #111827;
}

.che-card-body {
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.che-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.che-label {
  font-size: 13px;
  font-weight: 600;
  color: #374151;
}

.che-req {
  color: #dc2626;
}

.che-select {
  height: 42px;
  width: 100%;
  border-radius: 8px;
  border: 1px solid #d1d5db;
  background: #f9fafb;
  color: #111827;
  font-size: 14px;
  padding: 0 12px;
  cursor: pointer;
}

.che-select:focus {
  outline: none;
  border-color: #0076f5;
  box-shadow: 0 0 0 3px rgba(0, 118, 245, 0.12);
}

.che-hint {
  margin: 0;
  font-size: 12px;
  color: #6b7280;
  line-height: 1.5;
}

.che-mode-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.che-mode-option {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #111827;
  font-size: 13px;
  font-weight: 500;
}

.che-check-wrap {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-top: 2px;
  font-size: 13px;
  color: #374151;
}

.che-check {
  width: 14px;
  height: 14px;
}

.che-input {
  height: 42px;
  width: 100%;
  border-radius: 8px;
  border: 1px solid #d1d5db;
  background: #f9fafb;
  color: #111827;
  font-size: 14px;
  padding: 0 12px;
}

.che-input:focus {
  outline: none;
  border-color: #0076f5;
  box-shadow: 0 0 0 3px rgba(0, 118, 245, 0.12);
}

.che-customer-wrap {
  position: relative;
}

.che-customer-results {
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  right: 0;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  background: #ffffff;
  box-shadow: 0 10px 24px rgba(17, 24, 39, 0.12);
  max-height: 240px;
  overflow: auto;
  z-index: 20;
}

.che-customer-result {
  width: 100%;
  border: none;
  background: transparent;
  text-align: left;
  padding: 10px 12px;
  display: flex;
  justify-content: space-between;
  gap: 8px;
  font-size: 13px;
  color: #111827;
}

.che-customer-result:hover {
  background: #f3f4f6;
}

.che-customer-result--muted {
  color: #6b7280;
}

.che-customer-name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.che-customer-meta {
  color: #6b7280;
  flex-shrink: 0;
}

.che-selected-hint {
  font-size: 12px;
  color: #065f46;
  background: #ecfdf5;
  border: 1px solid #a7f3d0;
  border-radius: 6px;
  padding: 6px 8px;
}

/* ── Selected agent pill ── */
.che-agent-pill {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  border-radius: 8px;
  border: 1px solid #bbf7d0;
  background: #f0fdf4;
}

.che-agent-avatar {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: #d1fae5;
  color: #065f46;
  font-size: 15px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.che-agent-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
  min-width: 0;
}

.che-agent-name {
  font-size: 14px;
  font-weight: 600;
  color: #065f46;
}

.che-agent-email {
  font-size: 12px;
  color: #059669;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.che-agent-check {
  flex-shrink: 0;
}

/* ── Primary download button ── */
.che-dl-primary {
  height: 44px;
  border-radius: 10px;
  border: none;
  background: #111827;
  color: #ffffff;
  padding: 0 20px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  align-self: flex-start;
  white-space: nowrap;
}

.che-dl-primary:hover:not(:disabled) {
  background: #1f2937;
}
.che-dl-primary:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.che-dl-hint {
  margin: 0;
  font-size: 12px;
  color: #6b7280;
}

/* ── Side column: info + format ── */
.che-info-card {
  border-radius: 8px;
  border: 1px solid #dbeafe;
  background: #eff6ff;
  padding: 16px;
}

.che-info-icon {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;
}

.che-info-title {
  font-size: 13px;
  font-weight: 700;
  color: #1e40af;
}

.che-info-list {
  margin: 0;
  padding-left: 18px;
  font-size: 13px;
  color: #1d4ed8;
  line-height: 1.7;
}

.che-fmt-card {
  margin-top: 16px;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  background: #f9fafb;
  padding: 16px;
}

.che-fmt-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 700;
  color: #111827;
  margin-bottom: 12px;
}

.che-fmt-list {
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.che-fmt-list li {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 12px;
  color: #374151;
}

.che-fmt-col {
  display: inline-flex;
  align-items: center;
  padding: 1px 6px;
  border-radius: 4px;
  background: #e5e7eb;
  color: #111827;
  font-size: 11px;
  font-weight: 700;
  font-family: ui-monospace, monospace;
  flex-shrink: 0;
  min-width: 76px;
  justify-content: center;
}

/* ── Spin animation ── */
@keyframes che-spin {
  to {
    transform: rotate(360deg);
  }
}
.che-spin {
  animation: che-spin 1s linear infinite;
}

/* ── Dark override ── */
::global(body.dark) .che-page,
::global(body.dark) .che-shell,
::global(body.dark) .che-header,
::global(body.dark) .che-toolbar,
::global(body.dark) .che-card,
::global(body.theme-mix) .che-page,
::global(body.theme-mix) .che-shell,
::global(body.theme-mix) .che-header,
::global(body.theme-mix) .che-toolbar,
::global(body.theme-mix) .che-card {
  background: #ffffff !important;
  color: #111827 !important;
}

@media (max-width: 1100px) {
  .che-content {
    padding: 16px;
  }
  .che-toolbar {
    padding: 10px 16px 8px;
  }
  .che-grid {
    grid-template-columns: 1fr;
  }
}
</style>
